import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  DataGridPro,
  type GridCallbackDetails,
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridSortDirection,
  type GridSortItem,
  type GridSortModel,
} from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useIsPR1User } from '../../../hooks/permissions/useIsPR1User';
import { useIsPR2User } from '../../../hooks/permissions/useIsPR2User';

import type { TripLeg } from '../../../data/models/reservation.model';
import type { SortModel } from '../../../data/models/sort-model.model';
import type { Provider } from '../../../data/models/provider.model';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';

import * as accountingSlice from '../../../data/accounting.slice';
import * as providerSlice from '../../../data/provider.slice';
import * as triplegSlice from '../../../data/tripleg.slice';
import * as uiSlice from '../../../data/ui.slice';
import type { AssignTripLeg } from '../../../data/models/trip-leg-assign.model';

import SelectMenu from '../../forms/SelectMenu';
import TextSearch from '../../controls/TextSearch';
import triplegUtils from '../../../services/triplegUtils';

const PREFIX = 'AssignProviderDialog';

const classes = {
  content: `${PREFIX}-content`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.left}`]: {
    minWidth: 500,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.right}`]: {
    minWidth: 500,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface AssignProviderDialogProps {
  tripLeg: TripLeg;
  open: boolean;
  close: () => void;
  reassigningTripLeg?: boolean;
}

export default function AssignProviderDialog({
  tripLeg,
  open,
  close,
  reassigningTripLeg = false,
}: AssignProviderDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');
  const isPR1User = useIsPR1User();
  const isPR2User = useIsPR2User();

  const loadingProviders = useAppSelector(
    providerSlice.selectLoadingProviderList,
  );
  const providers = useAppSelector(providerSlice.selectProviderList);
  const transportationServices = useAppSelector(
    accountingSlice.selectTransportationServices,
  );
  const providerServicePricing = useAppSelector(
    accountingSlice.selectAllProviderServicePricings,
  );
  const savedTripLeg = useAppSelector(triplegSlice.selectSavedTripLeg);
  const savingTripLeg = useAppSelector(triplegSlice.selectSavingTripLeg);
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const providersMatchingTripLeg = useAppSelector(
    triplegSlice.selectProvidersMatchingTripLeg,
  );
  const loadingProvidersMatchingTripLeg = useAppSelector(
    triplegSlice.selectLoadingProvidersMatchingTripLeg,
  );
  const loadedProvidersMatchingTripLeg = useAppSelector(
    triplegSlice.selectLoadedProvidersMatchingTripLeg,
  );
  const allTripLegsInReservation = useAppSelector(
    triplegSlice.selectAllTripLegsInReservation,
  );
  const loadingAllTripLegsInReservation = useAppSelector(
    triplegSlice.selectLoadingAllTripLegsInReservation,
  );
  const loadedAllTripLegsInReservation = useAppSelector(
    triplegSlice.selectLoadedAllTripLegsInReservation,
  );
  // Track reassignment flow state
  const [rejectionInitiated, setRejectionInitiated] = useState(false);
  const [tripLegRejected, setTripLegRejected] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const selectedFilter = 'ctt0&active0'; // only get cleared and active providers
  const [searchFilter, setSearchFilter] = useState('');
  const defaultSortItem: GridSortItem = {
    field: 'transportationProviderName',
    sort: 'asc',
  };
  const [sortModel, setSortModel] = useState([defaultSortItem]);
  const [selectedProvider, setSelectedProvider] = useState<Provider>();
  const [requireProviderInfo, setRequireProviderInfo] = useState(false);
  const sortingOrder: GridSortDirection[] = ['desc', 'asc'];

  let servicesToDisplay = transportationServices;
  if (selectedProvider && providerServicePricing) {
    servicesToDisplay = transportationServices.filter((service) => {
      return providerServicePricing.some((pricing) => {
        return pricing.transportationServiceUUIDs.includes(
          service.transportationServiceUUID as string,
        );
      });
    });
  }

  const formHook = useForm({
    defaultValues: {
      ProviderTransportationService: '',
      CoverageArea: '',
    },
  });

  // Get eligible provider IDs when reassigning
  const eligibleProviderIDs = useMemo(() => {
    if (
      reassigningTripLeg &&
      loadedProvidersMatchingTripLeg &&
      providersMatchingTripLeg?.transportationProviderIDs?.length > 0
    ) {
      return providersMatchingTripLeg.transportationProviderIDs;
    }
    return undefined;
  }, [
    reassigningTripLeg,
    loadedProvidersMatchingTripLeg,
    providersMatchingTripLeg,
  ]);

  // Check if no eligible providers found when reassigning
  const noEligibleProvidersFound =
    reassigningTripLeg &&
    loadedProvidersMatchingTripLeg &&
    (!providersMatchingTripLeg?.transportationProviderIDs ||
      providersMatchingTripLeg.transportationProviderIDs.length === 0);

  const eligibleProviderCount = reassigningTripLeg ? providers.itemCount : 0;

  useEffect(() => {
    if (open && reassigningTripLeg && !rejectionInitiated) {
      setRejectionInitiated(true);
      dispatch(
        triplegSlice.rejectTripLegAsync({
          passengerUUID: (tripLeg as any).passengerUUID!,
          tripLegReferenceID: tripLeg.tripLegReferenceID!,
          tripLegRejectedReasonBC: 'REASSIGN',
        }),
      );
    }
  }, [open, reassigningTripLeg, rejectionInitiated]);

  useEffect(() => {
    if (reassigningTripLeg && savedTripLeg && !tripLegRejected) {
      setTripLegRejected(true);
      dispatch(triplegSlice.setSavedTripLeg(false));
      dispatch(
        triplegSlice.fetchAllTripLegsInReservationAsync(
          tripLeg.reservationReferenceID!,
          (tripLeg as any).passengerUUID!,
        ),
      );
    }
  }, [reassigningTripLeg, savedTripLeg, tripLegRejected]);

  useEffect(() => {
    if (
      reassigningTripLeg &&
      tripLegRejected &&
      loadedAllTripLegsInReservation &&
      allTripLegsInReservation?.length > 0
    ) {
      const thisTripLeg = allTripLegsInReservation.find(
        (tl) => tl.tripLegReferenceID === tripLeg.tripLegReferenceID,
      );
      if (thisTripLeg?.servicePlans) {
        const tripLegPayload =
          triplegUtils.getTripLegF1PayloadFromTransportResponse(tripLeg as any);
        tripLegPayload.servicePlans = thisTripLeg.servicePlans;
        dispatch(
          triplegSlice.fetchProvidersMatchingTripLegAsync(tripLegPayload),
        );
      }
    }
  }, [
    reassigningTripLeg,
    tripLegRejected,
    loadedAllTripLegsInReservation,
    allTripLegsInReservation,
  ]);

  useEffect(() => {
    if (reassigningTripLeg && !loadedProvidersMatchingTripLeg) {
      return;
    }

    const sortItem = sortModel.length > 0 ? sortModel[0] : defaultSortItem;
    const mappedSortModel: SortModel = {
      column: sortItem.field,
      direction: sortItem.sort,
    };
    dispatch(
      providerSlice.fetchProviderListAsync({
        pageSize,
        page,
        filter: selectedFilter,
        search: searchFilter,
        sortModel: mappedSortModel,
        transportationProviderIDs: eligibleProviderIDs,
      }),
    );
    dispatch(accountingSlice.fetchTransportationServicesAsync());
  }, [
    searchFilter,
    page,
    pageSize,
    sortModel,
    eligibleProviderIDs,
    reassigningTripLeg,
    loadedProvidersMatchingTripLeg,
  ]);

  useEffect(() => {
    if (selectedProvider) {
      dispatch(
        accountingSlice.fetchProviderServicePricingsAsync(
          selectedProvider.transportationProviderID,
        ),
      );
    }
  }, [selectedProvider]);

  useEffect(() => {
    if (saveSuccessMessage && savedTripLeg) {
      if (reassigningTripLeg && rejectionInitiated && !tripLegRejected) {
        return;
      }
      handleClose();
      dispatch(triplegSlice.setSavedTripLeg(false));
    }
  }, [
    saveSuccessMessage,
    savedTripLeg,
    reassigningTripLeg,
    rejectionInitiated,
    tripLegRejected,
  ]);

  const handleSave = (data: any) => {
    const assignInfo: AssignTripLeg = {
      // TODO: this is the wrong model and don't want to start changing models right now.
      //       the passenger attribute is in this model so I'm just casting it for now
      //       this needs to be fixed postMVP.
      passengerUUID: (tripLeg as any).passengerUUID!,
      tripLegReferenceID: tripLeg.tripLegReferenceID!,
      transportationProviderID: selectedProvider!.transportationProviderID,
      providerTransportationServiceUUID: data.ProviderTransportationService,
      coverageAreaID: data.CoverageArea || undefined,
      status: isPR1User || isPR2User ? TripLegStatus.Accepted : undefined,
    };
    let dispatchFn = triplegSlice.allocateTripLegToProviderAsync(assignInfo);
    if (isPR1User || isPR2User) {
      dispatchFn =
        providerSlice.manuallyAssignTripLegToProviderAsync(assignInfo);
    }
    dispatch(dispatchFn);
    if (tripLeg.isException) {
      dispatch(
        triplegSlice.clearTripLegExceptionAsync(tripLeg.tripLegReferenceID!),
      );
    }
  };

  const handleClose = () => {
    formHook.reset();
    setSelectedProvider(undefined);
    setRequireProviderInfo(false);
    if (reassigningTripLeg) {
      dispatch(triplegSlice.setLoadedProvidersMatchingTripLeg(false));
      dispatch(triplegSlice.setLoadedAllTripLegsInReservation(false));
      setRejectionInitiated(false);
      setTripLegRejected(false);
    }
    close();
  };

  const onSearchChange = (val: string) => {
    setSearchFilter(val);
  };

  const onPageChange = (pg: number) => {
    setPage(pg);
  };

  const onPageSizeChange = (pgSize: number) => {
    setPageSize(pgSize);
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== providers.currentPage - 1) {
      onPageChange(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setPage(1);
    setSortModel(model);
  };

  const selectRow = (row: GridRowSelectionModel) => {
    const providerId = row ? (row[0] as number) : undefined;
    const provider = providers?.items.find(
      (p) => p.transportationProviderID === providerId,
    );
    setSelectedProvider(provider);
    const tpIndex =
      tripLeg.transportationProviders?.findIndex(
        (tp) => tp.transportationProviderID === providerId,
      ) || -1;
    setRequireProviderInfo(tpIndex < 0);
  };

  const columns = [
    {
      field: 'transportationProviderName',
      headerName: t('triplegInventory.name') || '',
      flex: 2,
    },
  ];

  return (
    <RootDialog open={open} onClose={handleClose} maxWidth={'xl'}>
      <DialogTitle>
        {reassigningTripLeg
          ? t('triplegInventory.reassignTitle')
          : t('triplegInventory.assignTitle')}
      </DialogTitle>
      <DialogContent className={classes.content}>
        {/* Loading state: Step 1 - Rejecting current provider */}
        {reassigningTripLeg && savingTripLeg && !tripLegRejected && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight={200}
            width="100%"
            gap={2}>
            <CircularProgress />
            <Typography>
              {t('triplegInventory.rejectingCurrentProvider')}
            </Typography>
          </Box>
        )}

        {/* Loading state: Step 2 - Finding eligible providers (includes loading tripLegs data) */}
        {reassigningTripLeg &&
          tripLegRejected &&
          (loadingAllTripLegsInReservation ||
            loadingProvidersMatchingTripLeg) && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight={200}
              width="100%"
              gap={2}>
              <CircularProgress />
              <Typography>
                {t('triplegInventory.findingEligibleProviders')}
              </Typography>
            </Box>
          )}

        {/* No eligible providers found - only show after rejection */}
        {tripLegRejected && noEligibleProvidersFound && (
          <Box width="100%">
            <Alert severity="warning">
              {t('triplegInventory.noEligibleProvidersFound')}
            </Alert>
          </Box>
        )}

        {/* Provider selection - show when not reassigning, or after rejection + eligible providers loaded */}
        {(!reassigningTripLeg ||
          (tripLegRejected &&
            loadedProvidersMatchingTripLeg &&
            !noEligibleProvidersFound)) && (
          <Box width="100%">
            {reassigningTripLeg && eligibleProviderCount > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('triplegInventory.eligibleProvidersFound', {
                  count: eligibleProviderCount,
                })}
              </Alert>
            )}
            <Box className={classes.content}>
              <Box className={classes.left}>
                <Typography>{t('triplegInventory.provider')}</Typography>
                <TextSearch
                  id="text-search"
                  icon={<SearchIcon />}
                  label={t('triplegInventory.search')}
                  placeholder={t('triplegInventory.searchProvider')}
                  currentSearchFilter={searchFilter}
                  onSearchChange={onSearchChange}></TextSearch>

                <DataGridPro
                  getRowId={(row: any) => row.transportationProviderID}
                  columns={columns}
                  rows={providers.items}
                  rowCount={providers.itemCount}
                  paginationModel={{
                    page: providers.currentPage - 1,
                    pageSize,
                  }}
                  autoHeight={true}
                  paginationMode="server"
                  sortingMode="server"
                  pagination={true}
                  pageSizeOptions={[5]}
                  onPaginationModelChange={paginationChange}
                  sortingOrder={sortingOrder}
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChange}
                  loading={
                    loadingProviders ||
                    (reassigningTripLeg && loadingProvidersMatchingTripLeg)
                  }
                  checkboxSelection={false}
                  disableColumnMenu={true}
                  disableColumnSelector={true}
                  disableMultipleRowSelection={true}
                  onRowSelectionModelChange={selectRow}
                />
              </Box>
              <Box className={classes.right}>
                <SelectMenu
                  label={t('triplegInventory.providerService')}
                  name="ProviderTransportationService"
                  values={servicesToDisplay}
                  valueAttr="transportationServiceUUID"
                  labelAttr="name"
                  control={formHook.control}
                  required={requireProviderInfo}
                  rules={{
                    required: requireProviderInfo
                      ? requiredFieldMessage
                      : undefined,
                  }}
                />
                <SelectMenu
                  label={t('triplegInventory.coverageArea')}
                  name="CoverageArea"
                  values={
                    selectedProvider?.transportationProviderCoverageAreas?.map(
                      (ca) => ca.coverageArea,
                    ) || []
                  }
                  valueAttr="coverageAreaID"
                  labelAttr="coverageAreaName"
                  control={formHook.control}
                  required={requireProviderInfo}
                  rules={{
                    required: requireProviderInfo
                      ? requiredFieldMessage
                      : undefined,
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!formHook.formState.isValid || !selectedProvider}
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
